import { siteConfig } from "@acme/config";
import { createFileRoute, Link } from "@tanstack/react-router";
import { LegalPage, LegalSection } from "~/components/legal/legal-page";
import { publicCacheHeaders } from "~/lib/cache-headers";
import { seo } from "~/lib/seo";
import {
  generateStructuredDataGraph,
  getTermsOfServicePageSchemas,
} from "~/lib/structured-data";
import * as m from "~/paraglide/messages.js";

export const Route = createFileRoute("/(public)/terms-of-service")({
  component: RouteComponent,
  head: () => {
    const seoData = seo({
      description: m.meta_terms_description(),
      keywords:
        "Lindevo Terms of Service, Terms and Conditions, AI Systems, Software Development Terms, Legal",
      noindex: true,
      path: "/terms-of-service",
      title: m.meta_terms_title(),
    });
    const structuredData = generateStructuredDataGraph(
      getTermsOfServicePageSchemas()
    );
    return {
      links: seoData.links,
      meta: seoData.meta,
      scripts: [
        {
          children: structuredData,
          type: "application/ld+json",
        },
      ],
    };
  },
  headers: publicCacheHeaders,
});

/** Labels are localised; the ids are stable slugs other pages deep-link to. */
function getSections() {
  return [
    { id: "agreement", title: m.terms_section_1_title() },
    { id: "definitions", title: m.terms_section_2_title() },
    { id: "services", title: m.terms_section_3_title() },
    { id: "website-use", title: m.terms_section_4_title() },
    { id: "intellectual-property", title: m.terms_section_5_title() },
    { id: "your-information", title: m.terms_section_6_title() },
    { id: "third-party-links", title: m.terms_section_7_title() },
    { id: "disclaimer", title: m.terms_section_8_title() },
    { id: "liability", title: m.terms_section_9_title() },
    { id: "governing-law", title: m.terms_section_10_title() },
    { id: "changes", title: m.terms_section_11_title() },
    { id: "contact", title: m.terms_section_12_title() },
  ];
}

function RouteComponent() {
  return (
    <LegalPage
      label={m.terms_label()}
      lede={m.terms_last_updated()}
      sections={getSections()}
      title={m.terms_heading()}
    >
      <LegalSection id="agreement" title={m.terms_section_1_title()}>
        <p>{m.terms_section_1_p1()}</p>
      </LegalSection>

      <LegalSection id="definitions" title={m.terms_section_2_title()}>
        <ul>
          <li>
            <strong>{m.terms_section_2_term_us()}</strong> —{" "}
            {m.terms_section_2_term_us_desc()}{" "}
            <Link to="/legal">{m.terms_section_2_term_us_link()}</Link>.
          </li>
          <li>
            <strong>{m.terms_section_2_term_you()}</strong> —{" "}
            {m.terms_section_2_term_you_desc()}
          </li>
          <li>
            <strong>{m.terms_section_2_term_services()}</strong> —{" "}
            {m.terms_section_2_term_services_desc()}
          </li>
          <li>
            <strong>{m.terms_section_2_term_website()}</strong> —{" "}
            {m.terms_section_2_term_website_desc()}
          </li>
        </ul>
      </LegalSection>

      <LegalSection id="services" title={m.terms_section_3_title()}>
        <p>{m.terms_section_3_p1()}</p>
        <p>{m.terms_section_3_p2()}</p>
      </LegalSection>

      <LegalSection id="website-use" title={m.terms_section_4_title()}>
        <p>{m.terms_section_4_intro()}</p>
        <ul>
          <li>{m.terms_section_4_item_lawful()}</li>
          <li>{m.terms_section_4_item_scraping()}</li>
          <li>{m.terms_section_4_item_access()}</li>
          <li>{m.terms_section_4_item_damage()}</li>
        </ul>
        <p>{m.terms_section_4_closing()}</p>
      </LegalSection>

      <LegalSection
        id="intellectual-property"
        title={m.terms_section_5_title()}
      >
        <p>{m.terms_section_5_p1()}</p>
        <p>{m.terms_section_5_p2()}</p>
      </LegalSection>

      <LegalSection id="your-information" title={m.terms_section_6_title()}>
        <p>{m.terms_section_6_p1()}</p>
        <p>
          {m.terms_section_6_p2()}{" "}
          <Link to="/privacy-policy">{m.footer_privacy_policy()}</Link>.
        </p>
      </LegalSection>

      <LegalSection id="third-party-links" title={m.terms_section_7_title()}>
        <p>{m.terms_section_7_p1()}</p>
        <p>{m.terms_section_7_p2()}</p>
      </LegalSection>

      <LegalSection id="disclaimer" title={m.terms_section_8_title()}>
        <p>{m.terms_section_8_p1()}</p>
        <p>{m.terms_section_8_p2()}</p>
      </LegalSection>

      <LegalSection id="liability" title={m.terms_section_9_title()}>
        <p>{m.terms_section_9_p1()}</p>
        <p>{m.terms_section_9_p2()}</p>
      </LegalSection>

      <LegalSection id="governing-law" title={m.terms_section_10_title()}>
        <p>{m.terms_section_10_p1()}</p>
      </LegalSection>

      <LegalSection id="changes" title={m.terms_section_11_title()}>
        <p>{m.terms_section_11_p1()}</p>
        <p>{m.terms_section_11_p2()}</p>
      </LegalSection>

      <LegalSection id="contact" title={m.terms_section_12_title()}>
        <p>{m.terms_section_12_p1()}</p>
        <p>
          <a href={`mailto:${siteConfig.contacts.mail}`}>
            {siteConfig.contacts.mail}
          </a>
        </p>
      </LegalSection>
    </LegalPage>
  );
}
